import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DragDropContext, Droppable, Draggable,
} from 'react-beautiful-dnd';
import {
  ChevronLeft, ChevronRight, Plus, Check,
  Sparkles, MapPin, Gauge, CheckSquare, FileText, Users, Clock,
} from 'lucide-react';
import { useThemeColor } from '../../../hooks/useThemeColor';
import {
  fetchWidgetCatalog, fetchAgentLayout, saveAgentLayout,
  reorderLayout, toggleWidget, removeWidget, revertLayout,
  updateOverviewEnabled, updateShowProjectDetail,
} from '../../../slice/agentWidgetsSlice';
import WidgetCard from '../../../components/sub-components/WidgetCard';
import WidgetPromptEditor from '../../../components/sub-components/WidgetPromptEditor';
import { useTranslation } from '../../../i18n/LanguageContext';

const ICONS = {
  Sparkles, MapPin, Gauge, CheckSquare, FileText, Users, Clock,
};

const AvailableWidgetCard = ({ item, isAdded, onToggle, aispaceColor }) => {
  const { t } = useTranslation();
  const Icon = ICONS[item.icon] || Sparkles;
  return (
    <div className="shrink-0 w-[220px] bg-white rounded-xl border border-[#E5E7EB] p-4 flex flex-col">
      <div className="flex items-start gap-2 mb-2">
        <Icon size={18} className="text-[#6B7280] mt-0.5" />
        <div className="min-w-0">
          <p className="text-[14px] font-semibold text-[#212121] truncate">{(() => { const k = `widgets.titles.${item.key}`; const tr = t(k); return tr === k ? item.label : tr; })()}</p>
          <p className="text-[12px] text-[#6B7280] line-clamp-2">{item.description}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={onToggle}
        className="mt-auto w-full rounded-lg py-2 text-[13px] font-medium transition-colors flex items-center justify-center gap-1.5"
        style={
          isAdded
            ? { backgroundColor: '#F3F4F6', color: '#6B7280' }
            : { backgroundColor: aispaceColor, color: '#fff' }
        }
      >
        {isAdded ? (
          <>
            <Check size={14} /> {t('agentSettings.overviewAdded')}
          </>
        ) : (
          <>
            <Plus size={14} /> {t('agentSettings.overviewAdd')}
          </>
        )}
      </button>
    </div>
  );
};

const ProjectOverviewBuilder = ({ taskId: explicitTaskId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { aispaceColor } = useThemeColor();
  const selectedTask = useSelector((s) => s.tasks.selectedTask);
  const taskId = explicitTaskId || selectedTask?.id;
  const {
    catalog,
    layoutByTask, dirtyTasks, savingTaskId,
    overviewEnabledByTask, showProjectDetailByTask, layoutLoadingByTask,
  } = useSelector((s) => s.agentWidgets);
  const isLayoutLoading = taskId ? layoutLoadingByTask[taskId] === true : false;
  const overviewEnabled = taskId
    ? (overviewEnabledByTask[taskId] === true)
    : false;
  const showProjectDetail = taskId
    ? (showProjectDetailByTask[taskId] === true)
    : false;

  const [editingKey, setEditingKey] = useState(null);
  const railRef = useRef(null);

  useEffect(() => {
    if (!catalog || catalog.length === 0) dispatch(fetchWidgetCatalog());
  }, [catalog, dispatch]);

  useEffect(() => {
    if (taskId && !layoutLoadingByTask[taskId] && !layoutByTask[taskId]) {
      dispatch(fetchAgentLayout(taskId));
    }
  }, [taskId, dispatch, layoutLoadingByTask, layoutByTask]);

  const catalogByKey = useMemo(
    () => catalog.reduce((acc, c) => { acc[c.key] = c; return acc; }, {}),
    [catalog],
  );

  const layout = layoutByTask[taskId] || [];
  const enabledOrdered = useMemo(
    () => layout.filter(l => l.is_enabled).sort((a, b) => a.position - b.position),
    [layout],
  );
  const isDirty = Boolean(dirtyTasks[taskId]);
  const isSaving = savingTaskId === taskId;

  const handleRailScroll = (dir) => {
    if (!railRef.current) return;
    railRef.current.scrollBy({ left: dir * 260, behavior: 'smooth' });
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    dispatch(reorderLayout({
      taskId,
      fromIdx: result.source.index,
      toIdx: result.destination.index,
    }));
  };

  const handleSave = () => {
    dispatch(saveAgentLayout({ taskId, layout }));
  };

  const handleCancel = () => {
    dispatch(revertLayout(taskId));
    dispatch(fetchAgentLayout(taskId));
  };

  if (!taskId) {
    return <div className="p-8 text-center text-[#6B7280]">{t('agentSettings.overviewNoAgent')}</div>;
  }

  return (
    <div className="p-6 space-y-6 w-full min-w-0">
      {/* Per-agent enable toggle */}
      <section className="flex items-center justify-between bg-white border border-[#E5E7EB] rounded-xl px-5 py-4">
        <div className="min-w-0 pr-4">
          <h2 className="text-[16px] font-semibold text-[#212121]">{t('agentSettings.overviewEnableTitle')}</h2>
          <p className="text-[13px] text-[#6B7280] mt-1">{t('agentSettings.overviewEnableSubtitle')}</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={overviewEnabled}
          disabled={isLayoutLoading}
          onClick={() => !isLayoutLoading && dispatch(updateOverviewEnabled({ taskId, isEnabled: !overviewEnabled }))}
          className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: overviewEnabled ? aispaceColor : '#E5E7EB' }}
        >
          <span
            className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
            style={{ transform: overviewEnabled ? 'translateX(22px)' : 'translateX(2px)' }}
          />
        </button>
      </section>

      {/* Per-agent show project detail toggle — temporarily hidden */}

      {/* Available Widgets */}
      <section className={`w-full min-w-0 ${overviewEnabled ? '' : 'opacity-50 pointer-events-none'}`}>
        <h2 className="text-[18px] font-semibold text-[#212121]">{t('agentSettings.overviewAvailableWidgets')}</h2>
        <p className="text-[13px] text-[#6B7280] mt-1 mb-4">
          {t('agentSettings.overviewAvailableSubtitle')}
        </p>
        <div className="relative w-full min-w-0">
          <button
            type="button"
            onClick={() => handleRailScroll(-1)}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 bg-white border border-[#E5E7EB] rounded-full p-2 shadow-sm hover:bg-[#F9FAFB]"
          >
            <ChevronLeft size={16} className="text-[#6B7280]" />
          </button>
          <div className="w-full min-w-0 overflow-hidden">
            <div
              ref={railRef}
              className="flex gap-4 overflow-x-auto scroll-smooth px-1 pb-1"
              style={{ scrollbarWidth: 'none' }}
            >
              {catalog.map((item) => {
                const entry = layout.find(l => l.widget_key === item.key);
                const isAdded = Boolean(entry?.is_enabled);
                return (
                  <AvailableWidgetCard
                    key={item.key}
                    item={item}
                    isAdded={isAdded}
                    aispaceColor={aispaceColor}
                    onToggle={() => dispatch(toggleWidget({ taskId, widgetKey: item.key }))}
                  />
                );
              })}
            </div>
          </div>
          <button
            type="button"
            onClick={() => handleRailScroll(1)}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 bg-white border border-[#E5E7EB] rounded-full p-2 shadow-sm hover:bg-[#F9FAFB]"
          >
            <ChevronRight size={16} className="text-[#6B7280]" />
          </button>
        </div>
      </section>

      {/* Preview */}
      <section className={overviewEnabled ? '' : 'opacity-50 pointer-events-none'}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-[18px] font-semibold text-[#212121]">{t('agentSettings.overviewPreviewTitle')}</h2>
            <p className="text-[13px] text-[#6B7280] mt-1">
              {t('agentSettings.overviewPreviewSubtitle')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {isDirty && (
              <span className="text-[13px] text-[#DC2626]">{t('agentSettings.overviewUnsavedChanges')}</span>
            )}
            <button
              type="button"
              onClick={handleCancel}
              disabled={!isDirty || isSaving}
              className="px-4 py-2 border border-[#E5E7EB] rounded-lg text-[13px] text-[#212121] hover:bg-[#F9FAFB] disabled:opacity-50"
            >
              {t('agentSettings.overviewCancel')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!isDirty || isSaving}
              className="px-4 py-2 text-white rounded-lg text-[13px] font-medium hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: aispaceColor }}
            >
              {isSaving ? t('agentSettings.overviewSaving') : t('agentSettings.overviewSaveLayout')}
            </button>
          </div>
        </div>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="widget-preview" direction="horizontal">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="grid grid-cols-3 gap-4"
              >
                {enabledOrdered.map((entry, index) => {
                  const item = catalogByKey[entry.widget_key];
                  if (!item) return null;
                  return (
                    <Draggable key={entry.widget_key} draggableId={entry.widget_key} index={index}>
                      {(dragProvided) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                        >
                          <WidgetCard
                            catalogItem={item}
                            mode="preview"
                            aispaceColor={aispaceColor}
                            dragHandleProps={dragProvided.dragHandleProps}
                            onEditPrompt={item.is_llm ? () => setEditingKey(item.key) : undefined}
                            onRemove={() => dispatch(removeWidget({ taskId, widgetKey: item.key }))}
                          />
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
                {enabledOrdered.length === 0 && (
                  <div className="col-span-3 text-center text-[13px] text-[#9CA3AF] py-12 border-2 border-dashed border-[#E5E7EB] rounded-xl">
                    {t('agentSettings.overviewEmpty')}
                  </div>
                )}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </section>

      {editingKey && (
        <WidgetPromptEditor
          taskId={taskId}
          catalogItem={catalogByKey[editingKey]}
          aispaceColor={aispaceColor}
          onClose={() => setEditingKey(null)}
        />
      )}
    </div>
  );
};

export default ProjectOverviewBuilder;
